<template>
  <div class="fortschritt-container">
    <!-- Zugriffsbeschränkung -->
    <p v-if="!hasAccess" class="no-access">Kein Zugriff</p>

    <div v-else class="content-wrapper">
      <!-- ====================== PROJEKTAUSWAHL ======================= -->
      <template v-if="isTeacher || isAdmin">
        <label class="project-select-label">Projekt wählen:</label>
        <select v-model="selectedProjectId" class="project-select">
          <option
            v-for="p in selectableProjects"
            :key="p.projektId"
            :value="p.projektId"
          >
            {{ p.titel || 'Ohne Titel' }} (ID {{ p.projektId }})
          </option>
        </select>
      </template>

      <!-- ======================== HAUPTBEREICH ======================== -->
      <template v-if="currentProject">
        <h1 class="project-title">{{ currentProject.titel }}</h1>
        <div class="project-info">
          <p><strong>Betreuer:</strong> {{ currentProject.betreuerSamAccountName || 'Keine' }}</p>
          <p><strong>Status:</strong> {{ formattedStatus }}</p>
          <p><strong>Startdatum:</strong> {{ formatDate(currentProject.startdatum) }}</p>
          <p><strong>Enddatum:</strong> {{ formatDate(currentProject.enddatum) }}</p>
          <p><strong>Teammitglieder:</strong>
            <span v-if="teamMembers.length">{{ teamMembers.join(', ') }}</span>
            <span v-else>Keine</span>
          </p>
        </div>

        <!-- ======================= FORTSCHRITT ======================== -->
        <!-- ======================= FORTSCHRITT ======================== -->
        <section class="tracking-section">
          <h2>Fortschrittstracking</h2>

          <!-- Fortschrittselemente -->
          <div v-if="total" class="progress-wrapper">
            <div class="progress-bar">
              <div class="segment done"
                  :style="{ width: donePercent + '%' }"></div>
              <div class="segment working"
                  :style="{ width: workingPercent + '%' }"></div>
              <div class="segment open"
                  :style="{ width: openPercent + '%' }"></div>
            </div>

            <ul class="legend">
              <li><span class="dot done"></span> Abgeschlossen</li>
              <li><span class="dot working"></span> In Bearbeitung</li>
              <li><span class="dot open"></span> Ausstehend</li>
            </ul>

            <p class="progress-text">
              {{ done }} / {{ total }} Meilensteine abgeschlossen
              ({{ donePercent.toFixed(1) }} %)
            </p>
          </div>

          <!-- Hinweis, falls es noch keine Meilensteine gibt -->
          <p v-else class="no-progress">
            Füge Meilensteine hinzu, um deinen Fortschritt zu verfolgen.
          </p>
        </section>

        <!-- ===================== MEILENSTEINE ========================= -->
        <section class="milestones-section">
          <h2>Meilensteine</h2>
          <div v-if="isSchueler4" class="milestone-form">
            <input
              v-model="newMilestoneName"
              class="milestone-input"
              type="text"
              placeholder="Neuer Meilenstein-Name"
            />
            <button
              class="milestone-btn"
              @click="createMilestone"
              :disabled="!newMilestoneName.trim()"
            >
              Hinzufügen
            </button>
          </div>

          <table v-if="sortedMilestones.length" class="milestone-table">
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Meilenstein</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ms in sortedMilestones" :key="ms.meilensteinId">
                <td>{{ ms.meilensteinId }}</td>
                <td>{{ ms.name }}</td>
                <td>{{ formatStatus(ms.status) }}</td>
                <td>
                  <button
                    class="icon-button"
                    title="Bearbeiten"
                    @click="openEditMsModal(ms)"
                  >
                    <span class="material-symbols-outlined">edit</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-milestones">Noch keine Meilensteine vorhanden.</p>
        </section>

        <!-- ======================= DOKUMENTE ========================== -->
        <section class="documents-section">
          <h2>Dokumente</h2>

          <!-- Upload -->
          <div v-if="isSchueler4" class="upload-form">
            <input v-model="docTitle" class="upload-input" type="text" placeholder="Titel" />
            <input v-model="docDesc"  class="upload-input" type="text" placeholder="Beschreibung" />
            <input v-model="docType"  class="upload-input" type="text" placeholder="Typ" />
            <input v-model="docDate"  class="upload-input" type="date" />
            <input @change="handleFile" class="upload-input" type="file" />
            <button class="upload-btn" @click="uploadDocument">Dokument hochladen</button>
          </div>

          <!-- Tabelle -->
          <table
            v-if="projectDocuments.length"
            :class="{ 'document-table': true, 'hide-actions': !isAdmin }"
          >
            <thead>
              <tr>
                <th>Titel</th>
                <th>Dateiname</th>
                <th>Hochladungsdatum</th>
                <th>Ersteller</th>
                <th v-if="isAdmin">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="doc in projectDocuments" :key="doc.dokumentId">
                <td>{{ doc.titel }}</td>

                <!-- Download -->
                <td>
                  <a
                    href="#"
                    class="download-link"
                    @click.prevent="downloadDocument(doc.dokumentId)"
                    title="Herunterladen"
                  >
                    {{ doc.dateiname }}
                  </a>
                </td>

                <td>{{ formatDate(doc.hochladungsdatum) }}</td>
                <td>{{ doc.erstellerSamAccountName }}</td>

                <!-- ===== Nur Mülleimer (Admin) ===== -->
                <td
                  v-if="isAdmin"
                  class="actions-cell"
                >
                  <button
                    class="icon-button"
                    @click="confirmDeleteDocument(doc)"
                    title="Löschen"
                  >
                    <span class="material-symbols-outlined delete-icon">delete</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <p v-else class="no-documents">Noch keine Dokumente vorhanden.</p>
        </section>
      </template>

      <!-- Keine Projektauswahl -->
      <template v-else>
        <p class="status-text">Derzeit ist kein Projekt ausgewählt.</p>
      </template>
    </div>

    <!-- --------------- MEILENSTEIN-MODAL ---------------- -->
    <div
      v-if="showEditMsModal"
      class="modal-overlay"
      @click.self="closeEditMsModal"
    >
      <div class="modal-content">
        <button class="modal-close" @click="closeEditMsModal">
          <span class="material-symbols-outlined">close</span>
        </button>
        <h2>Meilenstein bearbeiten (#{{ currentMs?.meilensteinId }})</h2><br>
        <label>Neuer Name:</label>
        <input v-model="editMsName" type="text" class="modal-input" />
        <label style="margin-top:12px;">Neuer Status:</label>
        <select v-model="editMsStatus" class="modal-input">
          <option value="OFFEN">Offen</option>
          <option value="IN_BEARBEITUNG">in Bearbeitung</option>
          <option value="ERFUELLT" v-if="!isSchueler4">Erfüllt</option>
        </select>
        <div class="modal-actions">
          <button
            class="milestone-btn"
            :disabled="!nameChanged"
            @click="updateMsName"
          >Name speichern</button>
          <button
            class="milestone-btn"
            :disabled="!statusChanged"
            @click="updateMsStatus"
          >Status speichern</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import { useAuthenticationStore } from '@/stores/useAuthStore'

const formatGer = s => (s ? s.split('-').reverse().join('.') : '')

export default {
  name: 'Fortschritt',
  setup () {
    const authStore = useAuthenticationStore()
    const isStudent = computed(() => authStore.highestRole === 'ROLE_STUDENT')
    const isTeacher = computed(() => authStore.highestRole === 'ROLE_TEACHER')
    const isAdmin   = computed(() => authStore.highestRole === 'ROLE_ADMIN')
    const isSchueler4 = computed(() =>
      isStudent.value && authStore.roles.some(r =>
        ['schueler4AHIT', 'schueler4BHIT', 'schueler4CHIT', 'schueler4DHIT'].includes(r)
      )
    )
    const hasAccess = computed(() => isAdmin.value || isTeacher.value || isSchueler4.value)

    const formatStatus = s => {
      if (!s) return ''
      const text = s
        .toLowerCase()
        .replace(/ue/g, 'ü')
      return text
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    }
    /* ===================== PROJEKTE ===================== */
    const projects = ref([])
    const fetchProjects = async () => {
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      const resp = await axios.get(`${process.env.VUE_APP_BACKEND}/diplomdb/api/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      })
      projects.value = resp.data
    }

    const studentProject = computed(() =>
      isSchueler4.value
        ? projects.value.find(p => p.mitarbeiterSamAccountNames?.includes(authStore.samAccount)) || null
        : null
    )
    const teacherProjects = computed(() =>
      isTeacher.value
        ? projects.value.filter(p => p.betreuerSamAccountName === authStore.samAccount)
        : []
    )
    const selectableProjects = computed(() =>
      isAdmin.value ? projects.value : teacherProjects.value
    )
    const selectedProjectId = ref(null)
    const chosenProject = computed(() =>
      selectableProjects.value.find(p => p.projektId === selectedProjectId.value) ||
      selectableProjects.value[0] || null
    )
    const currentProject = computed(() => {
      if (isSchueler4.value) return studentProject.value
      if (isTeacher.value || isAdmin.value) return chosenProject.value
      return null
    })
    const teamMembers = computed(() =>
      currentProject.value ? currentProject.value.mitarbeiterSamAccountNames || [] : []
    )
    const formattedStatus = computed(() => {
      const raw = currentProject.value?.status || ''
      return raw.charAt(0) + raw.slice(1).toLowerCase()
    })

    /* ================= MEILENSTEINE ==================== */
    const milestones = ref([])
    const fetchMilestones = async () => {
      if (!currentProject.value) return
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${currentProject.value.projektId}/milestones`
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      })
      milestones.value = resp.data
    }

    const newMilestoneName = ref('')
    const sortedMilestones = computed(() =>
      [...milestones.value].sort((a, b) => a.meilensteinId - b.meilensteinId)
    )
    const done = computed(() => milestones.value.filter(m => m.status === 'ERFUELLT').length)
    const working = computed(() => milestones.value.filter(m => m.status === 'IN_BEARBEITUNG').length)
    const open = computed(() => milestones.value.filter(m => m.status === 'OFFEN').length)
    const total = computed(() => milestones.value.length)

    /* ======= Prozentwerte (Gleitkomma, mit Korrektur) ======= */
    const donePercent = computed(() =>
      total.value ? (done.value / total.value) * 100 : 0
    )
    const workingPercent = computed(() =>
      total.value ? (working.value / total.value) * 100 : 0
    )
    const openPercent = computed(() =>
      total.value ? Math.max(0, 100 - donePercent.value - workingPercent.value) : 0
    )

    /* ======================= CSRF ====================== */
    const csrfToken = ref('')
    const csrfHeaderName = ref('')
    const loadCsrf = async () => {
      if (csrfToken.value) return
      const resp = await axios.get(`${process.env.VUE_APP_BACKEND}/auth/csrf-token`, { withCredentials: true })
      csrfToken.value = resp.data.token
      csrfHeaderName.value = resp.data.headerName
    }

    /* =========== MEILENSTEIN-AKTIONEN ================== */
    const createMilestone = async () => {
      if (!isSchueler4.value || !newMilestoneName.value.trim()) return
      await loadCsrf()
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${currentProject.value.projektId}/milestones`
      await axios.post(url,
        { name: newMilestoneName.value.trim() },
        {
          headers: {
            'Content-Type': 'application/json',
            [csrfHeaderName.value]: csrfToken.value,
            Authorization: `Bearer ${token}`
          },
          withCredentials: true
        }
      )
      newMilestoneName.value = ''
      fetchMilestones()
    }

    /* =============== MODAL (MEILENSTEIN) =============== */
    const showEditMsModal = ref(false)
    const currentMs = ref(null)
    const editMsName = ref('')
    const editMsStatus = ref('')
    const nameChanged = computed(() =>
      currentMs.value && editMsName.value.trim() && editMsName.value.trim() !== currentMs.value.name
    )
    const statusChanged = computed(() =>
      currentMs.value && editMsStatus.value && editMsStatus.value !== currentMs.value.status
    )

    const openEditMsModal = ms => {
      currentMs.value = { ...ms }
      editMsName.value = ms.name
      editMsStatus.value = ms.status
      showEditMsModal.value = true
    }
    const closeEditMsModal = () => {
      showEditMsModal.value = false
      currentMs.value = null
    }

    const updateMsName = async () => {
      if (!nameChanged.value) return
      await loadCsrf()
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${currentProject.value.projektId}/milestones/${currentMs.value.meilensteinId}/name?newName=${encodeURIComponent(editMsName.value.trim())}`
      await axios.put(url, null, {
        headers: { [csrfHeaderName.value]: csrfToken.value, Authorization: `Bearer ${token}` },
        withCredentials: true
      })
      fetchMilestones()
      closeEditMsModal()
    }

    const updateMsStatus = async () => {
      if (!statusChanged.value) return
      await loadCsrf()
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${currentProject.value.projektId}/milestones/${currentMs.value.meilensteinId}/status?newStatus=${encodeURIComponent(editMsStatus.value)}`
      await axios.put(url, null, {
        headers: { [csrfHeaderName.value]: csrfToken.value, Authorization: `Bearer ${token}` },
        withCredentials: true
      })
      fetchMilestones()
      closeEditMsModal()
    }

    /* =================== DOKUMENTE ===================== */
    const documents = ref([])
    const fetchDocuments = async () => {
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      const resp = await axios.get(`${process.env.VUE_APP_BACKEND}/diplomdb/api/documents`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      })
      documents.value = resp.data
    }

    const projectDocuments = computed(() =>
      currentProject.value
        ? documents.value.filter(d => d.diplomarbeitId === currentProject.value.projektId)
        : []
    )

    /* -------------- Upload ---------------- */
    const docTitle = ref('')
    const docDesc  = ref('')
    const docType  = ref('')
    const docDate  = ref('')
    const docFile  = ref(null)

    const handleFile = e => { docFile.value = e.target.files[0] || null }

    const uploadDocument = async () => {
      if (!docFile.value) { alert('Bitte eine Datei auswählen!'); return }

      await loadCsrf()
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      const formData = new FormData()
      formData.append('titel', docTitle.value)
      formData.append('beschreibung', docDesc.value)
      formData.append('typ', docType.value)
      formData.append('datum', docDate.value)
      formData.append('diplomarbeitId', currentProject.value.projektId)
      formData.append('erstellerSamAccountName', authStore.samAccount)
      formData.append('file', docFile.value)

      try {
        await fetch(`${process.env.VUE_APP_BACKEND}/diplomdb/api/documents/upload`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            [csrfHeaderName.value]: csrfToken.value,
            Authorization: `Bearer ${token}`
          },
          body: formData
        })
        alert('Upload erfolgreich.')
        fetchDocuments()
        docTitle.value = docDesc.value = docType.value = docDate.value = ''
        docFile.value = null
      } catch { alert('Upload fehlgeschlagen. Bitte nochmal versuchen.') }
    }

    /* -------------- Download ---------------- */
    const downloadDocument = docId => {
      const url = `${process.env.VUE_APP_BACKEND}/diplomdb/api/documents/download/${encodeURIComponent(docId)}`
      window.open(url, '_blank')
    }

    /* -------------- Löschen ------------------ */
    const confirmDeleteDocument = async doc => {
      if (!confirm(`Dokument "${doc.dateiname}" wirklich löschen?`)) return
      await loadCsrf()
      const token = document.cookie.match(/authToken=([^;]+)/)?.[1] || ''
      try {
        await axios.delete(`${process.env.VUE_APP_BACKEND}/diplomdb/api/documents/${doc.dokumentId}`, {
          headers: { [csrfHeaderName.value]: csrfToken.value, Authorization: `Bearer ${token}` },
          withCredentials: true
        })
        fetchDocuments()
      } catch { alert('Löschen fehlgeschlagen.') }
    }

    /* =================== LIFECYCLE ==================== */
    onMounted(async () => {
      await authStore.checkAuth()
      if (hasAccess.value) {
        await fetchProjects()
        await fetchDocuments()
      }
    })

    watch(currentProject, p => {
      if (p) {
        fetchMilestones()
        fetchDocuments()
      }
    })

    /* ================== RETURN ======================== */
    return {
      /* Rechte / Rollen */
      hasAccess, isSchueler4, isTeacher, isAdmin,
      /* Projekte */
      selectableProjects, selectedProjectId,
      currentProject, teamMembers, formatDate: formatGer, formattedStatus,
      /* Fortschritt */
      done, working, open, total, donePercent, workingPercent, openPercent,
      /* Milestones */
      newMilestoneName, sortedMilestones, createMilestone,
      showEditMsModal, openEditMsModal, closeEditMsModal,
      currentMs, editMsName, editMsStatus,
      updateMsName, updateMsStatus,
      nameChanged, statusChanged,
      /* Dokumente */
      projectDocuments, docTitle, docDesc, docType, docDate,
      handleFile, uploadDocument,
      downloadDocument, confirmDeleteDocument, formatStatus
    }
  }
}
</script>

<style scoped>
/* ---------- Layout ---------- */
.fortschritt-container{display:flex;flex-direction:column;align-items:flex-start;min-height:90vh;font-family:'DM Sans',sans-serif;padding:20px;max-width:1100px;margin:0 auto;}
.content-wrapper{width:100%;background:#fff;border-radius:8px;padding:30px 40px;}
.no-access{font-size:1.2rem;color:#ef4444;font-weight:bold;text-align:center;width:100%;}
.project-select-label{font-weight:600;margin-right:8px;}
.project-select{padding:6px 10px;border:1px solid #ccc;border-radius:4px;margin-bottom:20px;}
.project-title{font-size:2rem;text-align:center;margin-bottom:0.5em;}
.project-info p{margin:4px 0;font-size:1rem;text-align:left;}
h2{margin-top:50px; margin-bottom: 15px;}
input, select, textarea, button {font-family:'DM Sans',sans-serif;}

/* ---------- Fortschritt ---------- */
.progress-bar{display:flex;height:24px;border-radius:4px;overflow:hidden;margin-bottom:8px;}
.segment{height:100%;}
.segment.done{background:#0000ff;}
.segment.working{background:#008cff;}
.segment.open{background:#6ce7ff;}
.legend{list-style:none;margin:12px 0 0;padding:0;}
.legend li{display:flex;align-items:center;gap:6px;font-size:0.9rem;margin-bottom:4px;}
.dot{width:12px;height:12px;border-radius:50%;}
.dot.done{background:#0000ff;}
.dot.working{background:#008cff;}
.dot.open{background:#6ce7ff;}

/* ---------- Tabellen ---------- */
.milestone-table,.document-table{width:100%;border-collapse:collapse;border-radius:8px;overflow:hidden;font-size:14px;background:#fff;box-shadow:0 2px 4px rgba(0,0,0,.1);}
.milestone-table thead tr,.document-table thead tr{background:#f4f4f4;}
.milestone-table th,.milestone-table td,.document-table th,.document-table td{padding:16px;text-align:center;border-bottom:1px solid #ddd;}
.milestone-table tbody tr:nth-child(odd),.document-table tbody tr:nth-child(odd){background:#f7f6fe;}
.milestone-table tbody tr:hover,.document-table tbody tr:hover{background:#eaeafc;}

/* --------------- Aktionen-Spalte (Dokumente) -------------- */
.document-table td.actions-cell{white-space:nowrap;padding:0 12px;}

/* ---------- Buttons & Icons ---------- */
.icon-button{
  background:none;
  border:none;
  cursor:pointer;
  padding:6px;
  margin:0 2px;
  font-size:1.3rem;
  line-height:1;
  color:#e11d48;                    /* Grundfarbe: Rot (nur Mülleimer) */
  transition:color .15s ease,transform .15s ease;
}
.icon-button:hover{
  color:#c41629;
  transform:scale(1.1);
}
.delete-icon{color:inherit;}        /* übernimmt Button-Farbe */

/* Klickbarer Download-Link */
.download-link{color:#2563eb;text-decoration:underline;cursor:pointer;}
.download-link:hover{color:#1e4ecb;}

/* ---------- Formulare ---------- */
.milestone-form,.upload-form{display:flex;flex-wrap:wrap;gap:10px;margin:10px 0 20px;}
.milestone-input,.upload-input{flex:1 1 180px;padding:8px 10px;border:1px solid #ccc;border-radius:4px;}
.milestone-btn,.upload-btn{padding:8px 14px;border:none;border-radius:4px;color:#fff;cursor:pointer;}
.milestone-btn{background:#5841ee;}
.milestone-btn:hover{background:#4a35d2;}
.milestone-btn:disabled{background:#9fb7f9;cursor:not-allowed;}
.upload-btn{background:#2563eb;}
.upload-btn:hover{background:#1e4ecb;}

/* ---------- Modal ---------- */
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:1000;}
.modal-content{background:#fff;padding:30px;border-radius:12px;max-width:700px;width:90%;box-shadow:0 2px 8px rgba(0,0,0,.2);position:relative;}
.modal-close{position:absolute;top:12px;right:12px;background:transparent;border:none;cursor:pointer;}
.modal-close .material-symbols-outlined{font-size:24px;color:#666;}
.modal-close:hover .material-symbols-outlined{color:#000;}
.modal-input{width:100%;padding:8px;margin-top:6px;border:1px solid #ccc;border-radius:4px;}
.modal-actions{display:flex;justify-content:flex-end;gap:8px;margin-top:20px;}

/* ---------- Platzhaltertexte ---------- */
.no-milestones,.no-documents,.no-progress{font-style:italic;color:#777;}

/* ---------- Fallback: alle Buttons ausblenden ------------ */
.hide-actions .icon-button,
.hide-actions .delete-icon{display:none!important;}
</style>
